import { getReferrerAttribution } from "@outschool/attribution";
import { Box } from "@outschool/backpack";
import { faGoogle } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { GoogleButton } from "@outschool/ui-components-shared";
import { useNavigation } from "@outschool/ui-utils";
import React from "react";

import * as Routes from "../../Routes";

/**
 * @see https://developers.google.com/identity/gsi/web/reference/js-reference#IdConfiguration
 */
interface IdConfiguration {
  client_id: string;
  callback: (response: CredentialResponse) => void;
}

type NotDisplayedReason =
  | "browser_not_supported"
  | "invalid_client"
  | "missing_client_id"
  | "opt_out_or_no_session"
  | "secure_http_required"
  | "suppressed_by_user"
  | "unregistered_origin"
  | "unknown_reason";

type SkippedReason =
  | "auto_cancel"
  | "user_cancel"
  | "tap_outside"
  | "issuing_failed";

type DismissedReason =
  | "credential_returned"
  | "cancel_called"
  | "flow_restarted";

type MomentType = "display" | "skipped" | "dismissed";

/**
 * @see https://developers.google.com/identity/gsi/web/reference/js-reference#PromptMomentNotification
 */
interface PromptMomentNotification {
  /**
   * 	Is this notification for a display moment?
   */
  isDisplayMoment: () => boolean;
  /**
   * Is this notification for a display moment, and the UI is displayed?
   */
  isDisplayed: () => boolean;
  /**
   * Is this notification for a display moment, and the UI isn't displayed?
   */
  isNotDisplayed: () => boolean;
  /**
   * The detailed reason why the UI isn't displayed.
   */
  getNotDisplayedReason: () => NotDisplayedReason;
  /**
   * Is this notification for a skipped moment?
   */
  isSkippedMoment: () => boolean;
  /**
   * The detailed reason for the skipped moment.
   */
  getSkippedReason: () => SkippedReason;
  /**
   * Is this notification for a dismissed moment?
   */
  isDismissedMoment: () => boolean;
  /**
   * The detailed reason for the dismissal.
   */
  getDismissedReason: () => DismissedReason;
  /**
   * Return a string for the moment type.
   */
  getMomentType: () => MomentType;
}

/**
 * @see https://developers.google.com/identity/gsi/web/reference/js-reference#GsiButtonConfiguration
 */
interface GsiButtonConfiguration {
  type?: "icon" | "standard";
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "small" | "medium" | "large";
  text?: "signin_with" | "signup_with" | "continue_with";
  shape?: "rectangular" | "pill" | "circle" | "square";
  logo_alignment?: "left" | "center";
  width?: number;
  locale?: string;
}

interface GoogleIdSDK {
  initialize: (config: IdConfiguration) => void;
  prompt: (
    momentListener?: (notification: PromptMomentNotification) => void
  ) => void;
  renderButton: (parent: HTMLElement, options: GsiButtonConfiguration) => void;
}

declare global {
  interface Window {
    google: {
      accounts: {
        id: GoogleIdSDK;
      };
    };
  }
}
/**
 * @see https://developers.google.com/identity/gsi/web/reference/js-reference#select_by
 */
type SelectBy =
  | "auto"
  | "user"
  | "user_1tap"
  | "user_2tap"
  | "btn"
  | "btn_confirm"
  | "btn_add_session"
  | "btn_confirm_add_session";

/**
 * @see https://developers.google.com/identity/gsi/web/reference/js-reference#CredentialResponse
 */
export interface CredentialResponse {
  credential: string;
  select_by: SelectBy;
}

interface ContinueWithGoogleProps {
  isLearnerApp?: boolean;
  trackingName: string;
  setAuthStrategy: (strategy: "google") => void;
  getAnonymousId?: () => Promise<string | null>;
}

export default function ContinueWithGoogle({
  isLearnerApp,
  trackingName,
  setAuthStrategy,
  getAnonymousId = () => Promise.resolve(null)
}: ContinueWithGoogleProps) {
  const { t } = useTranslation(
    "shared\\components\\google\\ContinueWithGoogle"
  );
  const navigate = useNavigation();
  const onClick = async () => {
    const anonymousId = await getAnonymousId();
    setAuthStrategy("google");
    const redirectUri = Routes.googleAuthRedirectUrl(isLearnerApp);
    const loginRedirectRoute = Routes.googleAuthUrl(redirectUri, {
      ...getReferrerAttribution(),
      anonymousId
    });
    // See http://caniuse.com/#feat=referrer-policy
    if (window && window.document) {
      const meta = window.document.createElement("meta");
      meta.setAttribute("name", "referrer");
      meta.setAttribute("content", "never");
      window.document.head.appendChild(meta);
    }
    // perform redirect
    navigate(loginRedirectRoute, { hardNav: true });
  };
  return (
    <GoogleButton
      onClick={onClick}
      trackingName={trackingName}
      sx={{
        padding: "0 !important",
        display: "flex"
      }}
    >
      <Box
        sx={{
          backgroundColor: "common.white",
          borderRadius: "50%",
          padding: "13px",
          width: "44px"
        }}
      >
        <img src={faGoogle} alt="" style={{ display: "block" }} />
      </Box>
      <Box
        sx={{
          padding: "12.5px 0",
          flex: 1,
          minWidth: "215px"
        }}
      >
        {t("Continue with Google")}
      </Box>
    </GoogleButton>
  );
}
